import { Copy, Lock, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { useDeleteRow } from '@/lib/time-queries'
import { rowLabel } from '@/lib/timesheet-copy'
import {
  deleteCopy,
  deleteDayBreakdown,
  deleteDescription,
  deleteTravelNote,
  rowDeleteAction,
} from '@/lib/timesheet-delete'
import type { PickerData, WeekRow } from '@/lib/types'
import { weekMetaFromId } from '@/lib/week'
import { AddRowButton, rowActionButtonClass } from './AddRowButton'
import type { NewRowSelection } from './week-rows'

interface RowActionsProps {
  row: WeekRow
  weekId: string
  personId?: string
  /** Er is voor deze regel al weggeschreven, ook al zijn de entry-ids nog niet binnen. */
  saved: boolean
  /** Getypte uren die nog niet zijn opgeslagen; alleen de dagtabel houdt die vast. */
  draftHours?: number
  /** De dag die in beeld staat. Aanwezig = dagweergave: verwijderen raakt alleen die dag. */
  dayIndex?: number
  /** Zelfde bestemming, lege regel: een tweede activiteit op ditzelfde werk. */
  onCopyRow: () => void
  /** Een nieuwe regel waarvan de bestemming nog gekozen moet worden. */
  onAddRow: (selection: NewRowSelection) => void
  pickers?: PickerData
  /** Haalt een regel weg die nog niets opgeslagen heeft. */
  onDropLocalRow: () => void
}

/**
 * De acties van een regel staan bij elkaar in een eigen kolom achteraan, op dezelfde plek
 * in de dag- en de weekweergave: een nieuwe regel (kies zelf de bestemming), dezelfde
 * bestemming nog een keer, en verwijderen. Die eerste twee zijn hetzelfde onder de motorkap
 * - een lege regel met een bestemming - en verschillen alleen in waar die bestemming
 * vandaan komt.
 *
 * Verwijderen reikt zover als de weergave: in de weekweergave gaat de hele weekregel weg,
 * in de dagweergave alleen de dag die je bekijkt. Hoeveel dat is staat in de bevestiging.
 */
export function RowActions({ row, weekId, personId, saved, draftHours, dayIndex, onCopyRow, onAddRow, pickers, onDropLocalRow }: RowActionsProps) {
  const [confirming, setConfirming] = useState(false)
  const deleteRow = useDeleteRow(weekId, personId)

  const action = rowDeleteAction(row, saved, draftHours, dayIndex)
  const lockedCell = (dayIndex === undefined ? row.cells : [row.cells[dayIndex]]).find((c) => c.locked)
  const label = rowLabel(row.clientName, row.projectName, row.categoryName)
  const meta = weekMetaFromId(weekId)
  const stake = action.kind === 'confirm' ? action.stake : null

  function requestDelete() {
    if (action.kind === 'blocked') return
    if (action.kind === 'local') {
      onDropLocalRow()
      return
    }
    if (action.kind === 'direct') {
      deleteRow.mutate({ entryIds: action.entryIds, label })
      return
    }
    setConfirming(true)
  }

  function confirmDelete() {
    if (!stake) return
    // Een concept staat nog nergens op de server; opruimen gebeurt dan lokaal.
    if (stake.draftOnly) {
      setConfirming(false)
      onDropLocalRow()
      return
    }
    deleteRow.mutate({ entryIds: stake.entryIds, label }, { onSuccess: () => setConfirming(false) })
  }

  const breakdown = stake ? deleteDayBreakdown(stake, (i) => meta.days[i].short) : null
  const travelNote = stake ? deleteTravelNote(stake) : null

  return (
    <>
      <div className="flex items-center justify-end gap-0.5">
        {pickers ? <AddRowButton pickers={pickers} onAdd={onAddRow} variant="icon" /> : null}
        <button
          type="button"
          onClick={onCopyRow}
          title="Zelfde bestemming, nog een regel"
          aria-label="Zelfde bestemming, nog een regel"
          className={`${rowActionButtonClass} hover:text-accent-700`}
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={requestDelete}
          disabled={action.kind === 'blocked' || deleteRow.isPending}
          title={
            action.kind === 'blocked'
              ? action.reason === 'saving'
                ? deleteCopy.saving
                : `Gefactureerd${lockedCell?.invoiceNumber ? ` op ${lockedCell.invoiceNumber}` : ''}, niet te verwijderen`
              : 'Regel verwijderen'
          }
          aria-label="Regel verwijderen"
          className={`${rowActionButtonClass} hover:text-status-overdue disabled:opacity-40`}
        >
          {lockedCell ? <Lock className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
        </button>
      </div>

      <Modal
        open={confirming}
        onOpenChange={setConfirming}
        title={deleteCopy.title}
        description={stake ? deleteDescription(stake, label, meta.label, (i) => meta.days[i].label) : undefined}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setConfirming(false)}>
              {deleteCopy.cancel}
            </button>
            <button
              type="button"
              className="btn-primary bg-status-overdue hover:bg-status-overdue/90"
              disabled={deleteRow.isPending}
              onClick={confirmDelete}
            >
              <Trash2 className="h-4 w-4" />
              {deleteRow.isPending ? deleteCopy.pending : deleteCopy.confirm}
            </button>
          </div>
        }
      >
        {breakdown || travelNote ? (
          <div className="space-y-1 text-sm text-ink-soft">
            {breakdown ? <p className="num">{breakdown}</p> : null}
            {travelNote ? <p>{travelNote}</p> : null}
          </div>
        ) : null}
      </Modal>
    </>
  )
}
